import React, { useEffect, useMemo, useState } from "react";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [priceRange, setPriceRange] = useState([6, 30]);
  const [sortBy, setSortBy] = useState("featured");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || p.description.toLowerCase().includes(s));
    }
    if (category !== "all") list = list.filter((p) => p.category === category);
    list = list.filter((p) => p.price_eur >= priceRange[0] && p.price_eur <= priceRange[1]);
    if (sortBy === "price-asc") list.sort((a, b) => a.price_eur - b.price_eur);
    else if (sortBy === "price-desc") list.sort((a, b) => b.price_eur - a.price_eur);
    else if (sortBy === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, search, category, priceRange, sortBy]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
      <SEO
        title="Tienda · Mimes Gourmet"
        description="Catálogo completo de mermeladas artesanas Mimes Gourmet: fresa, frutos rojos, cebolla con trufa, membrillo al café y más. Elaboradas en Graus, Huesca."
        path="/tienda"
        image="/products/frutos-rojos.webp"
        type="website"
      />
      <div className="mb-12">
        <p className="eyebrow mb-3">Colección completa</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[color:var(--brand-primary)] font-light tracking-tight">
          La <span className="italic text-[color:var(--brand-terracotta)]">tienda</span>
        </h1>
        <p className="mt-4 max-w-2xl text-[color:var(--brand-neutral)]">
          Elige los sabores que más te gusten a ti o para regalarlos a alguien que le gusta la mermelada artesana.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Filters */}
        <aside data-testid="catalog-filters" className="space-y-8">
          <div>
            <label className="eyebrow block mb-3">Buscar</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--brand-neutral)]/50" />
              <Input
                data-testid="filter-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerezas, trufa…"
                className="pl-9 bg-white border-[color:var(--brand-border)] focus-visible:ring-[color:var(--brand-sage)]"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-3">Tipo</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className="bg-white border-[color:var(--brand-border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="dulce">Dulces</SelectItem>
                <SelectItem value="salado">Salados</SelectItem>
                <SelectItem value="pack">Packs regalo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="eyebrow block mb-3">Precio ({priceRange[0]}€ – {priceRange[1]}€)</label>
            <Slider
              data-testid="filter-price"
              value={priceRange}
              onValueChange={setPriceRange}
              min={6}
              max={30}
              step={0.5}
              className="mt-2"
            />
          </div>
        </aside>

        {/* Grid */}
        <div>
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-[color:var(--brand-border)]">
            <p className="text-sm text-[color:var(--brand-neutral)]">
              <span data-testid="results-count" className="font-semibold text-[color:var(--brand-primary)]">{filtered.length}</span> productos
            </p>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="filter-sort" className="w-52 bg-white border-[color:var(--brand-border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Destacados</SelectItem>
                <SelectItem value="price-asc">Precio: menor a mayor</SelectItem>
                <SelectItem value="price-desc">Precio: mayor a menor</SelectItem>
                <SelectItem value="name">Nombre A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-[color:var(--brand-bg-alt)] rounded-md" />
                  <div className="mt-4 h-4 bg-[color:var(--brand-bg-alt)] rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="no-results" className="text-center py-24">
              <p className="font-serif text-2xl text-[color:var(--brand-primary)]">Sin resultados</p>
              <p className="text-sm text-[color:var(--brand-neutral)] mt-2">Prueba con otros filtros.</p>
            </div>
          ) : (
            <div data-testid="products-grid" className="grid grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
